import React, { useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { Unit } from '../services/api';

interface HierarchicalUnitSelectorProps {
  units: Unit[];
  selectedPropertyId: string | number;
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  showAllOption?: boolean; // Option to show "All Units" for filters
}

const HierarchicalUnitSelector: React.FC<HierarchicalUnitSelectorProps> = ({
  units,
  selectedPropertyId,
  value,
  onChange,
  label,
  disabled,
  error,
  helperText,
  showAllOption = false,
}) => {
  // Filter units for the selected property and build hierarchy
  const hierarchicalUnits = useMemo(() => {
    if (!selectedPropertyId) return [];
    
    const propertyUnits = units.filter(unit => 
      unit.property_id.toString() === selectedPropertyId.toString()
    );
    
    // Build hierarchical structure
    const buildHierarchy = (parentId: number | null = null, level: number = 0): Unit[] => {
      const children = propertyUnits.filter(unit => unit.parent_unit_id === parentId);
      
      return children.reduce((acc, unit) => {
        const unitWithLevel = { ...unit, level };
        acc.push(unitWithLevel);
        
        // Add children recursively
        const childUnits = buildHierarchy(unit.id, level + 1);
        acc.push(...childUnits);
        
        return acc;
      }, [] as (Unit & { level: number })[]);
    };
    
    return buildHierarchy();
  }, [units, selectedPropertyId]);

  // Removed unused getUnitDisplayName function as the display is handled in JSX

  // Get indentation based on level
  const getIndentation = (level: number) => level * 20;

  return (
    <FormControl fullWidth error={error}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        disabled={disabled}
        renderValue={(selected) => {
          if (!selected) return showAllOption ? 'All Units' : '';
          const selectedUnit = units.find(u => u.id.toString() === selected);
          return selectedUnit ? `${selectedUnit.unit_number} - ${selectedUnit.name}` : '';
        }}
      >
        {showAllOption && (
          <MenuItem value="">
            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
              All Units
            </Typography>
          </MenuItem>
        )}
        {hierarchicalUnits.map((unit) => (
          <MenuItem key={unit.id} value={unit.id.toString()}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Indentation spacing */}
              <Box sx={{ width: getIndentation(unit.level) }} />
              
              {/* Hierarchy indicator for sub-units */}
              {unit.level > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {/* Vertical line */}
                  <Box sx={{ 
                    width: 1, 
                    height: 16, 
                    bgcolor: 'divider',
                    mr: 0.5,
                  }} />
                  {/* Horizontal line */}
                  <Box sx={{ 
                    width: 8, 
                    height: 1, 
                    bgcolor: 'divider',
                    mr: 0.5,
                  }} />
                  {/* Level indicator */}
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    mr: 0.5,
                  }} />
                </Box>
              )}
              
              {/* Unit info */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <Typography variant="body2" component="span">
                  {unit.unit_number}
                </Typography>
                <Typography variant="body2" component="span" sx={{ ml: 1, opacity: 0.7 }}>
                  {unit.name}
                </Typography>
                
                {/* Level indicator chip for clarity */}
                {unit.level > 0 && (
                  <Chip
                    label={`L${unit.level}`}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 18,
                      fontSize: 10,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& .MuiChip-label': { px: 0.5 }
                    }}
                  />
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default HierarchicalUnitSelector; 